import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useVisitasCliente } from '../hooks/useVisitas';
import { VisitaDetailDrawer } from './VisitaDetailDrawer';
import type { Visita } from '../hooks/useVisitas';

// AIDEV-NOTE: Filtros de periodo para historico de visitas
const PERIODO_OPTIONS = [
  { value: '30', label: 'Ultimo mes' },
  { value: '90', label: 'Ultimos 3 meses' },
  { value: 'all', label: 'Tudo' }
] as const;

const TIPO_OPTIONS = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'prospeccao', label: 'Prospecao' },
  { value: 'manutencao', label: 'Manutencao' },
  { value: 'cobranca', label: 'Cobranca' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'outro', label: 'Outro' }
] as const;

interface HistoricoVisitasProps {
  clienteId: string;
}

function formatDuration(minutes?: number): string {
  if (!minutes) return '--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen) + '...';
}

function diasAtras(dateStr: string): number {
  const now = new Date();
  const d = new Date(dateStr);
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

export function HistoricoVisitas({ clienteId }: HistoricoVisitasProps) {
  const { data: visitas = [], isLoading } = useVisitasCliente(clienteId);
  const [periodo, setPeriodo] = useState('30');
  const [tipo, setTipo] = useState('all');
  const [drawerVisita, setDrawerVisita] = useState<Visita | null>(null);

  // Filtrar visitas por periodo e tipo
  const filteredVisitas = useMemo(() => {
    let result = [...visitas] as Visita[];

    if (periodo !== 'all') {
      const dias = parseInt(periodo, 10);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dias);
      result = result.filter((v) => new Date(v.checkin_at) >= cutoff);
    }

    if (tipo !== 'all') {
      result = result.filter((v) => v.tipo_visita === tipo);
    }

    // Ordenar cronologica reversa
    result.sort((a, b) => new Date(b.checkin_at).getTime() - new Date(a.checkin_at).getTime());
    return result;
  }, [visitas, periodo, tipo]);

  // Metricas
  const metricas = useMemo(() => {
    const ultimos30 = (visitas as Visita[]).filter((v) => diasAtras(v.checkin_at) <= 30);
    const duracoes = (visitas as Visita[])
      .map((v) => v.duracao_minutos)
      .filter((d): d is number => d != null && d > 0);
    const mediaMinutos =
      duracoes.length > 0 ? Math.round(duracoes.reduce((a, b) => a + b, 0) / duracoes.length) : 0;

    const sorted = [...visitas].sort(
      (a, b) => new Date(b.checkin_at).getTime() - new Date(a.checkin_at).getTime()
    );
    const firstVisita = sorted[0];
    const ultimaVisita = firstVisita ? diasAtras(firstVisita.checkin_at) : null;

    return {
      frequencia: ultimos30.length,
      mediaMinutos,
      ultimaVisitaDias: ultimaVisita
    };
  }, [visitas]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Metricas */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 140, textAlign: 'center' }}>
          <TrendingUpIcon fontSize="small" color="primary" />
          <Typography variant="h6">{metricas.frequencia}</Typography>
          <Typography variant="caption" color="text.secondary">
            visitas nos ultimos 30 dias
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 140, textAlign: 'center' }}>
          <AccessTimeIcon fontSize="small" color="primary" />
          <Typography variant="h6">~{metricas.mediaMinutos} min</Typography>
          <Typography variant="caption" color="text.secondary">
            duracao media
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 140, textAlign: 'center' }}>
          <CalendarTodayIcon fontSize="small" color="primary" />
          <Typography variant="h6">
            {metricas.ultimaVisitaDias != null ? `${metricas.ultimaVisitaDias}d` : '--'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            desde a ultima visita
          </Typography>
        </Paper>
      </Box>

      {/* Filtros */}
      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Periodo</InputLabel>
          <Select value={periodo} label="Periodo" onChange={(e) => setPeriodo(e.target.value)}>
            {PERIODO_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={tipo} label="Tipo" onChange={(e) => setTipo(e.target.value)}>
            {TIPO_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Lista de visitas */}
      {filteredVisitas.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            Nenhuma visita registrada para este cliente.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {filteredVisitas.map((visita) => (
            <Paper key={visita.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(visita.checkin_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDuration(visita.duracao_minutos)}
                    </Typography>
                  </Box>

                  {visita.tipo_visita && (
                    <Chip
                      label={visita.tipo_visita}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem' }}
                    />
                  )}

                  {visita.formulario?.observacoes && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {truncate(visita.formulario.observacoes, 100)}
                    </Typography>
                  )}

                  {visita.formulario?.tags && visita.formulario.tags.length > 0 && (
                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                      {visita.formulario.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setDrawerVisita(visita)}
                  sx={{ ml: 1, whiteSpace: 'nowrap' }}
                >
                  Detalhes
                </Button>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Drawer de detalhes */}
      <VisitaDetailDrawer
        open={!!drawerVisita}
        onClose={() => setDrawerVisita(null)}
        visita={drawerVisita}
      />
    </Box>
  );
}
